import type { DevTrace, TraceLevel } from "../types";

const TRACE_KEY = "dmi.analytics.development-traces";
const MAX_TRACES = 200;
const listeners = new Set<(traces: DevTrace[]) => void>();

const sensitiveKey = /password|secret|token|private.?key|authorization|cookie/i;

function redact(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redact);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        sensitiveKey.test(key) ? "[REDACTED]" : redact(item),
      ]),
    );
  }
  return value;
}

function current(): DevTrace[] {
  try {
    return JSON.parse(sessionStorage.getItem(TRACE_KEY) ?? "[]") as DevTrace[];
  } catch {
    return [];
  }
}

function publish(traces: DevTrace[]): void {
  listeners.forEach((listener) => listener(traces));
}

export const devTrace = {
  record(input: {
    runId: string;
    step: string;
    actor: string;
    level?: TraceLevel;
    summary: string;
    rationale?: string;
    inputs?: Record<string, unknown>;
    output?: Record<string, unknown>;
    durationMs?: number;
    phase?: string;
    layer?: string;
    service?: string;
    tool?: string;
    code?: string;
    detail?: Record<string, unknown>;
  }): DevTrace {
    const trace: DevTrace = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      level: "info",
      ...input,
      inputs: redact(input.inputs) as Record<string, unknown> | undefined,
      output: redact(input.output) as Record<string, unknown> | undefined,
      detail: redact(input.detail) as Record<string, unknown> | undefined,
    };
    const traces = [trace, ...current()].slice(0, MAX_TRACES);
    sessionStorage.setItem(TRACE_KEY, JSON.stringify(traces));
    publish(traces);

    const log = trace.level === "error" ? console.error : console.info;
    log("[DMI development trace]", trace);
    return trace;
  },

  list(): DevTrace[] {
    return current();
  },

  /**
   * Replaces the stored steps of every run a replay covers.
   *
   * Used to rebuild the trace of a conversation reopened from history. It
   * replaces rather than merges because a live step and its replayed twin
   * describe the same event but cannot be matched to each other: the live one
   * is identified by a generated id, the replayed one by the runtime's
   * (run, sequence). Merging them would show every step of the current
   * conversation twice. The stored events are the durable record, so they win.
   *
   * Runs the replay does not mention are left alone.
   */
  replaceRuns(replayed: DevTrace[]): DevTrace[] {
    const runs = new Set(replayed.map((trace) => trace.runId));
    const kept = current().filter((trace) => !runs.has(trace.runId));
    // Reversed because the store is newest first and a replay arrives
    // oldest first.
    const traces = [...replayed.slice().reverse(), ...kept].slice(0, MAX_TRACES);
    sessionStorage.setItem(TRACE_KEY, JSON.stringify(traces));
    publish(traces);
    return traces;
  },

  clear(): void {
    sessionStorage.removeItem(TRACE_KEY);
    publish([]);
  },

  subscribe(listener: (traces: DevTrace[]) => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
